import type { ImportJobDto, MenuSummaryDto } from "../../../shared/api-types";

// One state per import, read from the job AND the menu it filled (admin review 2026-11-02). The job stays "ready"
// forever once its draft exists; what happened afterwards (verified, published, changed again, archived) lives on
// the menu. Reading the job alone made the import page say "Nimic nu este publicat" next to an editor saying
// "Toate modificările sunt publicate".

export type ImportStage =
  | "uploading"
  | "processing"
  | "failed"
  | "blocked"
  | "preparing"
  | "draftError"
  | "needsReview"
  | "readyToPublish"
  | "published"
  | "publishedWithChanges"
  | "offline"
  | "menuGone";

export function importStage(job: ImportJobDto, menu: MenuSummaryDto | undefined): ImportStage {
  if (job.status === "uploading") return "uploading";
  if (job.status === "queued" || job.status === "processing") return "processing";
  if (job.status === "failed") return "failed";
  if (job.status === "blocked") return "blocked";
  if (job.status === "cancelled") return "failed";
  if (job.error) return "draftError";
  if (!job.menuId || !job.summary) return "preparing";
  if (!menu || menu.archived) return "menuGone";
  if (menu.publishedVersionId) {
    if (!menu.publicVisible) return "offline";
    return (menu.pendingChanges ?? 0) > 0 ? "publishedWithChanges" : "published";
  }
  return job.summary.needsReview > 0 ? "needsReview" : "readyToPublish";
}

export const STAGE_TEXT: Record<
  ImportStage,
  { title: string; tone: "info" | "success" | "warning" | "error" }
> = {
  uploading: { title: "Se încarcă fișierele", tone: "info" },
  processing: { title: "Se procesează", tone: "info" },
  failed: { title: "Importul a eșuat", tone: "error" },
  blocked: { title: "În așteptarea extragerii automate", tone: "info" },
  preparing: { title: "Documentul a fost citit, pregătim ciorna", tone: "info" },
  draftError: { title: "Ciorna nu a putut fi creată", tone: "error" },
  needsReview: { title: "Ciornă pregătită · verificare necesară", tone: "warning" },
  readyToPublish: { title: "Ciornă pregătită · nepublicată", tone: "success" },
  published: { title: "Publicat", tone: "success" },
  publishedWithChanges: { title: "Publicat · modificări nepublicate", tone: "warning" },
  offline: { title: "Retras din public", tone: "info" },
  menuGone: { title: "Meniul acestui import a fost arhivat", tone: "info" },
};

/** "Am pregătit 44 de produse în 10 categorii · 7 decizii · 12 corecturi" (the result before the files). */
export function importResultLine(s: NonNullable<ImportJobDto["summary"]>): string {
  const n = (k: number, one: string, many: string) =>
    k === 1 ? `1 ${one}` : `${k}${k >= 20 ? " de" : ""} ${many}`;
  return [
    `Am pregătit ${n(s.products, "produs", "produse")} în ${n(s.categories, "categorie", "categorii")}`,
    s.needsReview ? n(s.needsReview, "decizie de luat", "decizii de luat") : "nicio decizie de luat",
    s.autoFixes ? n(s.autoFixes, "corectură automată", "corecturi automate") : "",
  ]
    .filter(Boolean)
    .join(" · ");
}
